/**
 * Utility functions for inventory optimization and demand forecasting
 */

/**
 * Calculate Economic Order Quantity (EOQ)
 * @param {number} annualDemand - Annual demand in units
 * @param {number} orderCost - Cost per order
 * @param {number} holdingCost - Annual holding cost per unit
 * @returns {number} - Economic Order Quantity
 */
export const calculateEOQ = (annualDemand, orderCost, holdingCost) => {
  return Math.sqrt((2 * annualDemand * orderCost) / holdingCost);
};

/**
 * Calculate Reorder Point
 * @param {number} leadTimeDemand - Average demand during lead time
 * @param {number} safetyStock - Safety stock level
 * @returns {number} - Reorder point
 */
export const calculateReorderPoint = (leadTimeDemand, safetyStock) => {
  return leadTimeDemand + safetyStock;
};

/**
 * Calculate Safety Stock
 * @param {number} serviceLevel - Service level factor (z-score)
 * @param {number} leadTime - Lead time in days
 * @param {number} stdDevDemand - Standard deviation of daily demand
 * @returns {number} - Safety stock level
 */
export const calculateSafetyStock = (serviceLevel, leadTime, stdDevDemand) => {
  return serviceLevel * stdDevDemand * Math.sqrt(leadTime);
};

/**
 * Apply seasonal adjustments to forecast
 * @param {number} baselineForecast - Baseline forecast value
 * @param {number} seasonalIndex - Seasonal index for the period
 * @returns {number} - Seasonally adjusted forecast
 */
export const applySeasonalAdjustment = (baselineForecast, seasonalIndex) => {
  return baselineForecast * seasonalIndex;
};

/**
 * Apply promotional adjustments to forecast
 * @param {number} baselineForecast - Baseline forecast value
 * @param {number} promotionalLift - Expected lift from promotion (e.g., 1.2 for 20% increase)
 * @returns {number} - Promotion-adjusted forecast
 */
export const applyPromotionalAdjustment = (baselineForecast, promotionalLift) => {
  return baselineForecast * promotionalLift;
};

/**
 * Calculate inventory turnover ratio
 * @param {number} costOfGoodsSold - Cost of goods sold in a period
 * @param {number} averageInventory - Average inventory value in the same period
 * @returns {number} - Inventory turnover ratio
 */
export const calculateInventoryTurnover = (costOfGoodsSold, averageInventory) => {
  return costOfGoodsSold / averageInventory;
};

/**
 * Calculate days of supply
 * @param {number} inventoryLevel - Current inventory level
 * @param {number} averageDailyDemand - Average daily demand
 * @returns {number} - Days of supply
 */
export const calculateDaysOfSupply = (inventoryLevel, averageDailyDemand) => {
  return inventoryLevel / averageDailyDemand;
};

/**
 * Calculate fill rate
 * @param {number} demandSatisfied - Demand satisfied from stock
 * @param {number} totalDemand - Total demand
 * @returns {number} - Fill rate as a percentage
 */
export const calculateFillRate = (demandSatisfied, totalDemand) => {
  return (demandSatisfied / totalDemand) * 100;
};

/**
 * Calculate stockout probability
 * @param {number} inventoryLevel - Current inventory level
 * @param {number} leadTimeDemand - Expected demand during lead time
 * @param {number} stdDevLeadTimeDemand - Standard deviation of lead time demand
 * @returns {number} - Stockout probability
 */
export const calculateStockoutProbability = (inventoryLevel, leadTimeDemand, stdDevLeadTimeDemand) => {
  // This is a simplified calculation
  const z = (inventoryLevel - leadTimeDemand) / stdDevLeadTimeDemand;
  // Approximation of the complementary cumulative distribution function
  return 0.5 * Math.exp(-0.717 * z - 0.416 * z * z);
};

/**
 * Calculate ABC classification thresholds
 * @param {Array} items - Array of items with value property
 * @returns {Object} - Thresholds for A, B, and C categories
 */
export const calculateABCThresholds = (items) => {
  // Sort items by value in descending order
  const sortedItems = [...items].sort((a, b) => b.value - a.value);
  
  // Calculate cumulative value
  let totalValue = sortedItems.reduce((sum, item) => sum + item.value, 0);
  let cumulativeValue = 0;
  
  const thresholds = {
    A: null,
    B: null
  };
  
  // Find thresholds (A: top 80%, B: next 15%, C: bottom 5%)
  for (let i = 0; i < sortedItems.length; i++) {
    cumulativeValue += sortedItems[i].value;
    const percentOfTotal = cumulativeValue / totalValue;
    
    if (percentOfTotal >= 0.8 && !thresholds.A) {
      thresholds.A = sortedItems[i].value;
    }
    
    if (percentOfTotal >= 0.95 && !thresholds.B) {
      thresholds.B = sortedItems[i].value;
      break;
    }
  }
  
  return thresholds;
};